import { formatDate } from '@thoughtspot/date-format-util';
import { translate as t } from '@thoughtspot/i18n';
import { generateUUID } from '@thoughtspot/js-util';
import { ElementType } from '@thoughtspot/radiant-react/widgets/form-builder/interface';
import { IconColor, IconID, IconSize, } from '@thoughtspot/radiant-react/widgets/icon';
import { Link } from '@thoughtspot/radiant-react/widgets/link/link';
import { Colors, Typography, } from '@thoughtspot/radiant-react/widgets/typography';
import { getDocumentationLink } from '@thoughtspot/session-service';
import React from 'react';
import { SnapshotStatus, } from "../../../../services/generated/graphql-types";
import { ActionButtons } from '../../common/constants';
import styles from '../backup-storage.module.scss';
import { Action, LOCAL_MOUNT_POINT_REGEX, NasStatus, NEGATIVE_TTL_REGEX, SERVER_ADDRESS_REGEX, SNAPSHOT_NAME_REGEX, SnapshotStatus as SnapshotStatusLocales, TranslationsKeys, } from './constants';
const nasEmptyStateFormFields = () => [
    {
        formFields: [
            {
                id: 'emptyNas',
                elementType: ElementType.INPUT,
                config: {
                    type: 'text',
                    label: t(TranslationsKeys.STATUS),
                    isTextValueColorLight: true,
                },
                value: t(TranslationsKeys.NAS_NOT_CONFIGURED),
            },
        ],
    },
];
const snapshotEmptyStateFormFields = () => [
    {
        formFields: [
            {
                id: 'emptySnapshot',
                elementType: ElementType.INPUT,
                config: {
                    type: 'text',
                    label: t(TranslationsKeys.SNAPSHOT_CONFIGSTATUS_LABEL),
                    isTextValueColorLight: true,
                },
                value: t(TranslationsKeys.SNAPSHOT_NOT_CREATED),
            },
        ],
    },
];
const mountParamsHelpTextString = () => t(TranslationsKeys.HELP_TEXT_FOR_OPTIONS);
const mountParamsHelpText = () => (React.createElement(React.Fragment, null,
    mountParamsHelpTextString(),
    ' ',
    React.createElement(Link, { href: getDocumentationLink('reference/tscli-command-ref.html'), target: "_blank" }, t(TranslationsKeys.LEARN_MORE))));
const optionalMountParameters = () => t(TranslationsKeys.OPTIONAL_MOUNT_PARAMETERS);
const optional = () => t(TranslationsKeys.OPTIONAL);
const mountParamsLabel = () => (React.createElement("div", null,
    optionalMountParameters(),
    ' ',
    React.createElement(Typography, { variant: "footnote", color: Colors.grayLight, overrideTag: "span", className: styles.optionalLabelText },
        "(",
        optional(),
        ")")));
const nasFormFields = () => [
    {
        id: 'mount_type',
        elementType: ElementType.RADIO,
        config: {
            children: [
                { label: t(TranslationsKeys.NFS), value: 'nfs' },
                { label: t(TranslationsKeys.CIFS), value: 'cifs' },
            ],
            label: t(TranslationsKeys.MOUNT_TYPE),
            orientation: 'tray',
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.MOUNT_TYPE),
            }),
        },
        value: 'nfs',
    },
    {
        id: 'server_address',
        elementType: ElementType.INPUT,
        config: {
            type: 'text',
            label: t(TranslationsKeys.SERVER_ADDRESS),
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.SERVER_ADDRESS),
            }),
            regex: SERVER_ADDRESS_REGEX,
            regexError: t(TranslationsKeys.SERVER_ADDRESS_REGEX_ERROR),
        },
        value: '',
    },
    {
        id: 'path_on_server',
        elementType: ElementType.INPUT,
        config: {
            type: 'text',
            label: t(TranslationsKeys.PATH_ON_SERVER),
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.PATH_ON_SERVER),
            }),
        },
        value: '',
    },
    {
        id: 'mount_point',
        elementType: ElementType.INPUT,
        config: {
            type: 'text',
            label: t(TranslationsKeys.LOCAL_MOUNT_POINT),
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.LOCAL_MOUNT_POINT),
            }),
            regex: LOCAL_MOUNT_POINT_REGEX,
            regexError: t(TranslationsKeys.LOCAL_MOUNT_POINT_REGEX_ERROR),
        },
        value: '',
    },
    {
        id: 'options',
        elementType: ElementType.INPUT,
        config: {
            type: 'text',
            label: mountParamsLabel(),
            helpText: mountParamsHelpText(),
        },
        value: '',
    },
];
const snpshotFormFields = () => [
    {
        id: 'name',
        elementType: ElementType.INPUT,
        config: {
            type: 'text',
            label: t(TranslationsKeys.SNAPSHOT_FORM_LABEL_FOR_NAME),
            helpText: t(TranslationsKeys.HELP_TEXT_FOR_SNAPSHOT_NAME),
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.SNAPSHOT_FORM_LABEL_FOR_NAME),
            }),
            regex: SNAPSHOT_NAME_REGEX,
            regexError: t(TranslationsKeys.SNAPSHOT_REGEX_ERROR),
        },
        value: '',
    },
    {
        id: 'ttl',
        elementType: ElementType.INPUT,
        config: {
            type: 'number',
            label: t(TranslationsKeys.TTL_LABEL),
        },
        validation: {
            required: true,
            requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                field_name: t(TranslationsKeys.TTL_LABEL),
            }),
            regex: NEGATIVE_TTL_REGEX,
            regexError: t(TranslationsKeys.TTL_REGEX_ERROR),
        },
        value: '',
    },
];
const initialSnapshotConfig = () => [
    {
        id: 'snapshot',
        actions: [
            {
                type: ActionButtons.EDIT,
                title: t('snapshot.configure.title'),
            },
        ],
        title: t('adminSection.backup.snapshot'),
        formFields: [...snpshotFormFields()],
    },
];
const nasInititalConfig = () => [
    {
        id: 'nas',
        actions: [
            {
                type: ActionButtons.EDIT,
                title: t('nas.configure.btnLabel'),
            },
        ],
        title: t(TranslationsKeys.MOUNT_POINT_NAS_HEADING),
        formFields: [...nasFormFields()],
    },
];
const removeUsernamePassword = (state, action) => {
    const userNameConfigArrayToRemove = action.payload.filter((form) => form.id === 'username');
    if (userNameConfigArrayToRemove.length > 0) {
        const optionalFormFields = action.payload.slice(-1);
        const remainingFields = action.payload.slice(0, action.payload.length - 1);
        return {
            ...state,
            formFields: [
                ...remainingFields.slice(0, remainingFields.length - 2),
                ...optionalFormFields,
            ],
        };
    }
    return state;
};
const addUsernamePassword = (state, action) => {
    const userNameConfigArray = action.payload.filter((form) => form.id === 'username');
    if (userNameConfigArray.length === 0) {
        const usernameFieldConfig = {
            id: 'username',
            elementType: ElementType.INPUT,
            config: {
                type: 'text',
                label: t(TranslationsKeys.USERNAME_LABEL),
            },
            validation: {
                required: true,
                requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                    field_name: t(TranslationsKeys.USERNAME_LABEL),
                }),
            },
            value: '',
        };
        const passwordFieldConfig = {
            id: 'password',
            elementType: ElementType.INPUT,
            config: {
                type: 'password',
                label: t(TranslationsKeys.PASSWORD_LABEL),
            },
            validation: {
                required: true,
                requiredError: t(TranslationsKeys.VALID_FIELDNAME, {
                    field_name: t(TranslationsKeys.PASSWORD_LABEL),
                }),
            },
            value: '',
        };
        const optionalFormFieldsForAddUsernamePwd = action.payload.slice(-1);
        const remainingFieldsForAddUsernamePwd = action.payload.slice(0, action.payload.length - 1);
        return {
            ...state,
            formFields: [
                ...remainingFieldsForAddUsernamePwd,
                usernameFieldConfig,
                passwordFieldConfig,
                ...optionalFormFieldsForAddUsernamePwd,
            ],
        };
    }
    return state;
};
const reducer = (state, action) => {
    switch (action.type) {
        case Action.EMPTY_NAS:
            return {
                ...state,
                actions: state.actions.map((stateAction) => ({
                    ...stateAction,
                    title: t('nas.configure.btnLabel'),
                })),
            };
        case Action.NOT_EMPTY_NAS:
            return {
                ...state,
                actions: state.actions.map((stateAction) => ({
                    ...stateAction,
                    title: t('nas.addPoint'),
                })),
            };
        case Action.REMOVE_USERNAME_PASSWORD:
            return removeUsernamePassword(state, action);
        case Action.ADD_USERNAME_PASSWORD:
            return addUsernamePassword(state, action);
        case Action.VALIDATE_SNAPSHOT_FORM_DUPLICATE_NAME:
            return {
                ...state,
                formFields: action.payload.map((formField) => formField.id === 'name'
                    ? {
                        ...formField,
                        validation: {
                            ...formField.validation,
                            error: action.errorMessage,
                        },
                        valid: false,
                    }
                    : formField),
            };
        case Action.RESET_NAS_FORM:
            return {
                ...state,
                formFields: action.payload.map((formField) => ({
                    ...formField,
                    value: formField.id === 'mount_type' ? 'nfs' : '',
                    valid: true,
                })),
            };
        case Action.RESET_SNAPSHOT_FORM:
            return {
                ...state,
                formFields: action.payload.map((formField) => ({
                    ...formField,
                    value: '',
                    valid: true,
                })),
            };
        default:
            return state;
    }
};
const actions = {
    primary: {
        type: ActionButtons.SAVE,
        text: 'Save',
        returnData: true,
    },
    secondary: {
        type: ActionButtons.CANCEL,
        text: 'Cancel',
        returnData: true,
    },
};
const DATE_FORMAT_STRING = 'EEEE, d MMMM yyyy, t ZZZZ';
const getNasStatusIconAndColor = (status) => {
    let icon = IconID.EXCLAMATION_POINT_CIRCLE;
    let iconColor = IconColor.RED;
    if (status === 'CONFIGURED') {
        icon = IconID.CHECKMARK_CIRCLE;
        iconColor = IconColor.GREEN;
    }
    else if (status === 'NOT_CONFIGURED') {
        iconColor = IconColor.YELLOW;
    }
    return { icon, iconColor };
};
const getSnapshotStatusIconAndColor = (snapshotStatus) => {
    let icon = IconID.EXCLAMATION_POINT_CIRCLE;
    let iconColor = IconColor.RED;
    if (snapshotStatus === SnapshotStatus.SnapshotCreateFinished) {
        icon = IconID.CHECKMARK_CIRCLE;
        iconColor = IconColor.GREEN;
    }
    else if (snapshotStatus === SnapshotStatus.SnapshotCreateStarted ||
        snapshotStatus === SnapshotStatus.SnapshotDeleteScheduled) {
        iconColor = IconColor.YELLOW;
    }
    return { icon, iconColor };
};
const createSnapShotRowData = (snapshotRawData, rowDeleteFunction) => {
    if (!snapshotRawData || !snapshotRawData.length) {
        return [];
    }
    return snapshotRawData.map((element) => ({
        id: element.name,
        snapShotName: {
            href: '',
            text: element.name,
        },
        snapShotScheduledOn: {
            text: formatDate(element.start_timestamp_seconds * 1000, DATE_FORMAT_STRING),
        },
        snapshotType: {
            text: `${element.type}`,
        },
        snapshotStatus: {
            icon: getSnapshotStatusIconAndColor(element.snapshotStatus).icon,
            iconSize: IconSize.MEDIUM,
            iconColor: getSnapshotStatusIconAndColor(element.snapshotStatus)
                .iconColor,
            text: `${SnapshotStatusLocales[element.snapshotStatus]}`,
        },
        deleteAction: {
            rowDeleteFunction: () => rowDeleteFunction(element.name),
        },
    }));
};
const createNasRowData = (nasRawData, nasRowDeleteFunction) => {
    if (!nasRawData || !nasRawData.length) {
        return [];
    }
    return nasRawData.map((nasItem) => ({
        id: generateUUID(),
        nasIpLink: {
            text: `${nasItem.server_address}`,
        },
        nasMountType: {
            text: `${nasItem.mount_type}`,
        },
        serverPath: {
            text: `${nasItem.path_on_server}`,
        },
        localMountPoint: {
            text: nasItem.mount_point,
        },
        isConfiguredLogo: {
            icon: getNasStatusIconAndColor(nasItem.status).icon,
            iconSize: IconSize.MEDIUM,
            iconColor: getNasStatusIconAndColor(nasItem.status).iconColor,
            text: `${NasStatus[nasItem.status]}`,
        },
        deleteAction: {
            nasRowDeleteFunction: () => nasRowDeleteFunction(nasItem.mount_point),
        },
    }));
};
export { createNasRowData, createSnapShotRowData, actions, reducer, nasInititalConfig, initialSnapshotConfig, nasEmptyStateFormFields, snapshotEmptyStateFormFields, };
//# sourceMappingURL=backup-storage.util.js.map